/**
 * Tasks Tab Component
 * Displays and manages the task list with filters
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ListTodo, Plus } from 'lucide-react';
import { EnhancedTaskItem } from '../EnhancedTaskPanel';

export const TasksTab = ({
  tasks,
  filter,
  setFilter,
  pendingCount,
  onToggleTask,
  onExpandTask,
  onAddTask
}) => {
  // Filtered tasks
  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'pending') return task.status !== 'completed';
    if (filter === 'completed') return task.status === 'completed';
    if (filter === 'high') return task.priority === 'high' && task.status !== 'completed';
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {['all', 'pending', 'high', 'completed'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              className="text-xs whitespace-nowrap"
              onClick={() => setFilter(f)}
              data-testid={`task-filter-${f}`}
            >
              {f === 'all' ? 'All' : f === 'high' ? 'Urgent' : f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'pending' && pendingCount > 0 && (
                <Badge variant="secondary" className="ml-1.5 text-[10px]">{pendingCount}</Badge>
              )}
            </Button>
          ))}
        </div>
        <Button size="sm" onClick={onAddTask} className="shrink-0" data-testid="add-task-btn">
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Add Task</span>
        </Button>
      </div>
      
      {/* Tasks List */}
      <div className="space-y-2">
        {filteredTasks.map(task => (
          <EnhancedTaskItem 
            key={task.id} 
            task={task} 
            onToggle={onToggleTask}
            onExpand={onExpandTask}
          />
        ))}
        {filteredTasks.length === 0 && (
          <Card className="glass-card">
            <CardContent className="py-12 text-center">
              <ListTodo className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">No tasks found</p>
              <Button className="mt-4" onClick={onAddTask} data-testid="add-first-task-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Task
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TasksTab;
